import {
  ApplicationConfig,
  provideZoneChangeDetection,
  provideAppInitializer,
  inject,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { ICON_SETTINGS } from '@progress/kendo-angular-icons';
import { appRoutes } from './app.routes';
import { reducers, metaReducers } from './store';
import { DomainsEffects } from './store/entities/domains/domains.effects';
import { UserEffects } from './store/entities/user/user.effects';
import { FeaturesEffects } from './store/entities/features/features.effects';
import { ProfileEffects } from './store/entities/profile/profile.effects';
import { DomainChangeCoordinatorEffects } from './store/coordinators/domain-change.coordinator.effects';
import { UserDomainsEffects } from './store/entities/user-domains/user-domains.effects';
import { TagsEffects } from './store/entities/tags/tags.effects';
import { AttributesEffects } from './store/entities/attributes/attributes.effects';
import { QRTemplatesEffects } from './store/entities/qr-templates/qr-templates.effects';
import { LinkTemplatesEffects } from './store/entities/link-templates/link-templates.effects';
import { PendingLinksEffects } from './store/entities/pending-links/pending-links.effects';
import {
  authInterceptor,
  authErrorInterceptor,
  headersInterceptor,
  domainInterceptor,
  csrfInterceptor,
  forbiddenInterceptor,
} from './core/interceptors';
import { AppInitService } from './core/services/app-init.service';

function initializeApp(): Promise<void> {
  const appInitService = inject(AppInitService);

  return appInitService.initialize(['domains', 'user', 'profile']);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    { provide: ICON_SETTINGS, useValue: { type: 'svg' } },
    provideRouter(appRoutes),
    provideHttpClient(
      withInterceptors([
        headersInterceptor,
        csrfInterceptor,
        authInterceptor,
        authErrorInterceptor,
        forbiddenInterceptor, // Handle 403 errors after auth errors
        domainInterceptor,
      ])
    ),
    provideStore(reducers, { metaReducers }),
    provideEffects([
      DomainsEffects,
      UserEffects,
      FeaturesEffects,
      ProfileEffects,
      DomainChangeCoordinatorEffects,
      UserDomainsEffects,
      TagsEffects,
      QRTemplatesEffects,
      LinkTemplatesEffects,
      PendingLinksEffects,
      AttributesEffects,
    ]),
    provideAppInitializer(initializeApp),
  ],
};
